import { DialogHTMLAttributes, FC, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import useCurrentState from '../../hooks/dialog/useCurrentState';
import usePortal from '../../hooks/dialog/usePortal';

import BackDrop from './BackDrop';
import s from './Modal.module.scss';
import ModalBody from './ModalBody';
import { ModalConfig, ModalContext } from './ModalContext';
import ModalFooter from './ModalFooter';
import ModalHeader from './ModalHeader';
import ModalWrapper, { ModalWrapperProps } from './ModalWrapper';

interface Props {
  open?: boolean;
  closeButton?: boolean;
  animated?: boolean;
  width?: 'default' | 'small' | 'large';
  preventClose?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

type NativeAttrs = Omit<DialogHTMLAttributes<unknown>, keyof Props>;

export type ModalProps = Props & NativeAttrs & ModalWrapperProps;

const Modal: FC<React.PropsWithChildren<React.PropsWithChildren<ModalProps>>> = ({
  children,
  onClose,
  onOpen,
  open,
  width: wrapperWidth = 'default',
  preventClose = false,
  fullScreen,
  animated = true,
  closeButton = false,
  ...props
}) => {
  const portal = usePortal('modal');
  const [visible, setVisible, visibleRef] = useCurrentState<boolean>(false);
  const [rebound, setRebound] = useState(false);

  const closeModal = () => {
    if (onClose) {
      onClose();
    }
    setVisible(false);
  };

  useEffect(() => {
    if (open === undefined) return;
    if (open && onOpen) {
      onOpen();
    }
    if (!open && visibleRef.current && onClose) {
      onClose();
    }
    setVisible(open);
  }, [open, onClose, onOpen, setVisible, visibleRef]);

  const toggleRebound = () => {
    setRebound(true);
    const timer = setTimeout(() => {
      setRebound(false);
      clearTimeout(timer);
    }, 300);
  };

  const closeFromBackdrop = () => {
    if (preventClose) {
      toggleRebound();
      return;
    }
    closeModal();
  };

  const modalConfig: ModalConfig = useMemo(
    () => ({
      close: closeModal
    }),
    []
  );

  if (!portal) return null;
  return createPortal(
    <ModalContext.Provider value={modalConfig}>
      <BackDrop onClick={closeFromBackdrop} visible={visible} maxWidth={wrapperWidth} scss={s}>
        <ModalWrapper visible={visible} rebound={rebound} animated={animated} closeButton={closeButton} {...props}>
          {children}
        </ModalWrapper>
      </BackDrop>
    </ModalContext.Provider>,
    portal
  );
};

type ModalComponent<P> = FC<React.PropsWithChildren<React.PropsWithChildren<P>>> & {
  Header: typeof ModalHeader;
  Body: typeof ModalBody;
  Footer: typeof ModalFooter;
};

Modal.displayName = 'Modal';

export default Modal as ModalComponent<ModalProps>;
